import type { ColumnVisibilityItem } from "providers/Auth";

export enum AnalyticsTab {
  // BoomCRM and BoomScreen used to be two tabs; they are one "Leasing"
  // dashboard now — CRM modules first, screening modules after (PLT-578).
  "LEASING" = "Leasing",
  // LEGACY ANALYTICS TABS: the two the Leasing dashboard replaced, still used
  // by `legacy/` while the `leasing-dashboard` flag is off.
  "APPLICATIONS" = "Applications",
  "CRM" = "CRM",
  "SCREENING_OUTCOMES" = "Screening outcomes",
  "BOOM_REPORT" = "Boom Report"
}
export enum AnalyticsCard {
  LEADS_CREATED = "Leads created",
  DAYS_ON_MARKET = "Days on market",
  TIME_TO_MANUAL_CONTACT = "Time to manual contact",
  TOUCH_POINTS_PER_LEAD = "Touch points per lead",
  SHOWINGS_SCHEDULED = "Showings scheduled",
  SHOWINGS_COMPLETED = "Showings completed",
  SHOWINGS_NO_SHOWS = "Showing no shows",
  PRESCREENING_FAILURES = "Pre-screening criteria failures",
  TIME_TO_DECISION = "Time to decision"
}

export type ShowingsAnalyticsCard =
  | AnalyticsCard.SHOWINGS_SCHEDULED
  | AnalyticsCard.SHOWINGS_COMPLETED
  | AnalyticsCard.SHOWINGS_NO_SHOWS;

export const BOOM_LISTING_COLOR = "#E6007E";

export enum UnitTab {
  Available = "available",
  All = "all"
}

export const DEFAULT_PER_PAGE = Number(
  window.localStorage.getItem("per_page") || 10
);

export const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const THREE_COLS_DRILLDOWN_GRID_CLASSNAME =
  "grid grid-cols-[1fr_1fr_120px] items-center gap-4";
export const FOUR_COLS_DRILLDOWN_GRID_CLASSNAME =
  "grid grid-cols-[1fr_130px_130px_120px] gap-3 items-center";
export const FIVE_COLS_DRILLDOWN_GRID_CLASSNAME =
  "grid grid-cols-[1fr_110px_130px_260px_140px] gap-3 items-center";
export const DRILLDOWN_ONE_DATE_GRID_CLASSNAME =
  "grid grid-cols-[1fr_1fr_140px_100px] items-center gap-4";
export const DRILLDOWN_TWO_DATE_GRID_CLASSNAME =
  "grid grid-cols-[1fr_1fr_120px_140px_120px] items-center gap-4";

export const DRILLDOWN_ROWS_BASE_CLASSNAME =
  "px-5 py-3 bg-content-1000-inverted hover:bg-alpha-20 cursor-pointer";
export const DRILLDOWN_COLUMNS = [
  { label: "Name" },
  { label: "Property" },
  { label: "Unit" }
];

export const SHOWINGS_DRILLDOWN_COLUMNS = [
  { label: "Name" },
  { label: "Unit" },
  { label: "Assigned to" },
  { label: "Date", className: "text-right" }
];

export const SHOWINGS_NO_SHOWS_DRILLDOWN_COLUMNS = [
  { label: "Name" },
  { label: "Unit" },
  { label: "Assigned to" },
  { label: "Reason" },
  { label: "Date", className: "text-right" }
];

export const formatDays = (days: number) =>
  `${days} ${days === 1 ? "day" : "days"}`;

export const formatHours = (hours: number) =>
  `${hours} ${hours === 1 ? "hr" : "hrs"}`;

export const formatMinutes = (seconds: number) => {
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

/**
 * The unit the applications overview counts in. Partners switch between the two from the
 * switcher in the table header; the choice rides in the analytics `filters` payload as
 * `counting_unit` and reaches both the metric tiles and the table.
 *
 * The toggle changes the grain only — which application groups are in play is still decided by
 * the page filters, so `applicants` expands exactly those groups into their individual
 * applicants. `application_groups` is the API's default and what the page has always shown.
 */
export const COUNTING_UNIT = {
  APPLICATION_GROUPS: "application_groups",
  APPLICANTS: "applicants"
} as const;

export type CountingUnit = (typeof COUNTING_UNIT)[keyof typeof COUNTING_UNIT];

export const DEFAULT_COUNTING_UNIT: CountingUnit =
  COUNTING_UNIT.APPLICATION_GROUPS;

export const COUNTING_UNIT_TABS: { value: CountingUnit; label: string }[] = [
  { value: COUNTING_UNIT.APPLICANTS, label: "Applicants" },
  { value: COUNTING_UNIT.APPLICATION_GROUPS, label: "Application groups" }
];

export const ANALYTICS_COLUMNS_TAB_KEY = "analytics_applications";

/**
 * Canonical default order, labels, and always-on flags for the Analytics
 * Applications table. Each `id` matches the TanStack column id used in
 * `slices/AnalyticsTable.tsx`, and the list is kept in sync with the backend
 * `ANALYTICS_DEFAULT_COLUMNS` constant in
 * `boompay-api/app/models/boom/partner/user.rb`.
 */
export const DEFAULT_ANALYTICS_COLUMNS: ColumnVisibilityItem[] = [
  {
    id: "id",
    label: "Group ID",
    checked: true,
    disabled: false,
    always_on: true
  },
  {
    id: "applicants_names",
    label: "Applicant names",
    checked: true,
    disabled: false,
    always_on: true
  },
  { id: "criteria", label: "Criteria", checked: true, disabled: false },
  { id: "status", label: "Status", checked: true, disabled: false },
  { id: "city", label: "City", checked: true, disabled: false },
  { id: "state", label: "State", checked: false, disabled: false },
  { id: "property", label: "Property", checked: true, disabled: false },
  { id: "unit", label: "Unit", checked: true, disabled: false },
  { id: "invited_at", label: "Invited", checked: false, disabled: false },
  { id: "started_at", label: "Started", checked: true, disabled: false },
  {
    id: "submitted_at",
    label: "Submitted",
    checked: false,
    disabled: false
  },
  { id: "updated_at", label: "Last updated", checked: false, disabled: false },
  {
    id: "decision_at",
    label: "Decision made/Canceled",
    checked: false,
    disabled: false
  },
  {
    id: "verification_template",
    label: "Template",
    checked: false,
    disabled: false
  },
  {
    id: "desired_move_in_date",
    label: "Requested move-in",
    checked: false,
    disabled: false
  },
  {
    id: "credit_score",
    label: "Main applicant's credit score",
    checked: false,
    disabled: false
  },
  {
    id: "dob",
    label: "Main applicant's DOB",
    checked: false,
    disabled: false
  },
  {
    id: "number_of_applicants",
    label: "Applicants",
    checked: false,
    disabled: false
  },
  {
    id: "number_of_guarantors",
    label: "Guarantors",
    checked: false,
    disabled: false
  },
  {
    id: "number_of_occupants",
    label: "Occupants",
    checked: false,
    disabled: false
  }
];

export const ANALYTICS_APPLICANT_COLUMNS_TAB_KEY = "analytics_applicants";

/**
 * Default order, labels, and always-on flags for the Analytics table under the applicants
 * counting unit. Kept in sync with the backend `ANALYTICS_APPLICANTS_DEFAULT_COLUMNS` constant in
 * `boompay-api/app/models/boom/partner/user.rb` — an id missing there is dropped from the saved
 * preferences on read.
 *
 * It is a separate tab key from the groups table because the rows are applicants: the two sets
 * share almost no ids, so one key would discard whichever view the user was not last on.
 */
export const DEFAULT_ANALYTICS_APPLICANT_COLUMNS: ColumnVisibilityItem[] = [
  {
    id: "application_id",
    label: "Group ID",
    checked: true,
    disabled: false,
    always_on: true
  },
  {
    id: "name",
    label: "Applicant",
    checked: true,
    disabled: false,
    always_on: true
  },
  { id: "email", label: "Email", checked: true, disabled: false },
  { id: "role", label: "Role", checked: true, disabled: false },
  { id: "status", label: "Status", checked: true, disabled: false },
  { id: "city", label: "City", checked: true, disabled: false },
  { id: "state", label: "State", checked: false, disabled: false },
  { id: "property", label: "Property", checked: true, disabled: false },
  { id: "unit", label: "Unit", checked: true, disabled: false },
  { id: "started_at", label: "Started", checked: true, disabled: false },
  { id: "submitted_at", label: "Submitted", checked: false, disabled: false },
  { id: "updated_at", label: "Last updated", checked: false, disabled: false },
  {
    id: "decision_at",
    label: "Decision made/Canceled",
    checked: false,
    disabled: false
  },
  {
    id: "verification_template",
    label: "Template",
    checked: false,
    disabled: false
  },
  {
    id: "desired_move_in_date",
    label: "Requested move-in",
    checked: false,
    disabled: false
  },
  {
    id: "credit_score",
    label: "Credit score",
    checked: false,
    disabled: false
  },
  { id: "dob", label: "DOB", checked: false, disabled: false }
];
